import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sparkles, ArrowRight, Brain, FileSearch, MessageSquareText, UserRoundSearch,
  Briefcase, Shield, Globe, Zap, Users, BarChart3,
  Check, Star, Menu, X, Table2, FileSpreadsheet, TrendingUp,
  ShieldCheck, Target, Heart, Layers, Database, LineChart, RefreshCw,
  Gauge, LayoutDashboard, UserCheck, AlertTriangle, Replace, MonitorSmartphone,
  Send, Phone, Building2, Mail, ChevronDown, Activity, Linkedin
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [demoForm, setDemoForm] = useState({ name: '', email: '', company: '', phone: '', team_size: '', message: '' });
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoSubmitted, setDemoSubmitted] = useState(false);
  const [cms, setCms] = useState(null);

  useEffect(() => {
    // Fetch CMS + plans once on mount. No external deps; empty array is correct.
    axios.get(`${API}/plans/public`).then(r => setPlans(r.data)).catch(() => {});
    axios.get(`${API}/platform/cms/landing`).then(r => setCms(r.data)).catch(() => {});
  }, []);

  const [productsOpen, setProductsOpen] = useState(false);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  // Auto-scroll to hash anchor on mount (e.g., /landing#demo from pricing page)
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, []);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setDemoSubmitting(true);
    try {
      await axios.post(`${API}/demo-requests`, demoForm);
      setDemoSubmitted(true);
      setDemoForm({ name: '', email: '', company: '', phone: '', team_size: '', message: '' });
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setDemoSubmitting(false);
    }
  };

  const getPrice = (plan) => {
    if (currency === 'INR') {
      const p = plan.price_monthly_inr ?? plan.price_monthly * 83;
      return { symbol: '\u20B9', amount: p.toLocaleString('en-IN') };
    }
    return { symbol: '$', amount: plan.price_monthly };
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white overflow-x-hidden" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => scrollTo('hero')}>
            <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Recruitizr</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-slate-400">
            <button onClick={() => scrollTo('usps')} className="hover:text-white transition-colors">Why Recruitizr</button>
            {/* Products Dropdown */}
            <div className="relative" onMouseEnter={() => setProductsOpen(true)} onMouseLeave={() => setProductsOpen(false)}>
              <button className="flex items-center gap-1 hover:text-white transition-colors" data-testid="nav-products-dropdown">
                Products <ChevronDown className={`h-3.5 w-3.5 transition-transform ${productsOpen ? 'rotate-180' : ''}`} />
              </button>
              {productsOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50">
                  <div className="bg-[#12121a] border border-white/10 rounded-xl shadow-2xl shadow-black/50 p-2 w-64" data-testid="products-dropdown-menu">
                    {[
                      { icon: Brain, label: 'RecruiterIQ', desc: 'AI-powered recruitment tools', path: '/features/recruiter-iq', color: 'text-violet-400' },
                      { icon: Zap, label: 'HireTrack', desc: 'End-to-end recruitment pipeline', path: '/features/hiretrack', color: 'text-cyan-400' },
                      { icon: Users, label: 'HRMS', desc: 'People management suite', path: '/features/hrms', color: 'text-emerald-400' },
                      { icon: Activity, label: 'NextMove', desc: 'Predictive analytics', path: '/features/nextmove', color: 'text-indigo-400' },
                      { icon: Database, label: 'TalentVault', desc: 'AI candidate database', path: '/features/talentvault', color: 'text-amber-400' },
                    ].map(item => (
                      <button key={item.path} onClick={() => { navigate(item.path); setProductsOpen(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-left">
                        <item.icon className={`h-4 w-4 ${item.color} shrink-0`} />
                        <div>
                          <p className="text-sm font-medium text-white">{item.label}</p>
                          <p className="text-xs text-slate-500">{item.desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <a href="/pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="/blog" className="hover:text-white transition-colors" data-testid="nav-blog-link">Blog</a>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Button variant="ghost" className="text-slate-300 hover:text-white" onClick={() => navigate('/login')} data-testid="nav-login-btn">Log In</Button>
            <Button className="bg-violet-600 hover:bg-violet-500 text-white rounded-full px-6" onClick={() => scrollTo('demo')} data-testid="nav-get-started-btn">
              Get Started <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0a0a0f] border-t border-white/5 p-4 space-y-3">
            <button onClick={() => { scrollTo('usps'); setMobileMenuOpen(false); }} className="block w-full text-left text-slate-300 py-2">Why Recruitizr</button>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 px-1 pb-1">Products</p>
            {[
              { label: 'RecruiterIQ', path: '/features/recruiter-iq' },
              { label: 'HireTrack', path: '/features/hiretrack' },
              { label: 'HRMS', path: '/features/hrms' },
              { label: 'NextMove', path: '/features/nextmove' },
              { label: 'TalentVault', path: '/features/talentvault' },
            ].map(item => (
              <button key={item.path} onClick={() => { navigate(item.path); setMobileMenuOpen(false); }} className="block w-full text-left text-slate-300 py-2 pl-3">{item.label}</button>
            ))}
            <div className="border-t border-white/5 pt-2">
              <a href="/pricing" className="block w-full text-left text-slate-300 py-2">Pricing</a>
              <a href="/blog" className="block w-full text-left text-slate-300 py-2" data-testid="mobile-nav-blog-link">Blog</a>
            </div>
            <Button className="w-full bg-violet-600 text-white" onClick={() => navigate('/login')}>Log In</Button>
          </div>
        )}
      </nav>

      {/* ========== HERO ========== */}
      <section id="hero" className="relative pt-24 pb-14 md:pt-44 md:pb-32 px-6">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(124,58,237,0.15),transparent_70%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:48px_48px]" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/20 mb-6 px-4 py-1.5 text-sm" data-testid="hero-badge">
            <Sparkles className="h-3 w-3 mr-2" /> {cms?.hero?.badge || 'CRM + ATS + AI in one platform'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[1.08] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {cms?.hero?.title || "Your recruitment firm's"}
            <br />
            <span className="bg-gradient-to-r from-violet-400 via-indigo-400 to-cyan-400 bg-clip-text text-transparent">
              {cms?.hero?.title_highlight || 'operating system.'}
            </span>
          </h1>

          {/* Prominent Tagline */}
          <div className="mb-8 inline-flex items-center gap-3 px-6 py-3 rounded-full border border-violet-500/30 bg-violet-500/[0.07] backdrop-blur-sm" data-testid="hero-tagline">
            <div className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" />
            <p className="text-base md:text-lg font-semibold text-violet-300 tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {cms?.hero?.tagline || 'Built by recruiters, for recruiters — tested on real mandates daily.'}
            </p>
          </div>

          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            {cms?.hero?.description || 'Stop juggling spreadsheets, disconnected tools, and manual follow-ups. Recruitizr brings your CRM, ATS, and AI intelligence into one platform — so your team closes more, faster.'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              className="bg-violet-600 hover:bg-violet-500 text-white rounded-full px-8 py-6 text-base font-semibold shadow-[0_0_30px_rgba(124,58,237,0.3)]"
              onClick={() => scrollTo('demo')}
              data-testid="hero-cta-btn"
            >
              {cms?.hero?.cta_primary || 'Get Started'} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="border-slate-700 text-slate-300 hover:bg-white/5 rounded-full px-8 py-6 text-base"
              onClick={() => scrollTo('recruiteriq')}
            >
              {cms?.hero?.cta_secondary || 'Meet RecruiterIQ'} <Brain className="ml-2 h-5 w-5" />
            </Button>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-slate-500">
            {(cms?.hero?.bullets || ['Dedicated database per tenant', 'Replace 5+ tools on day one', 'India-resident data']).map(b => (
              <span key={b} className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> {b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ========== 5 TOOLS REPLACED SECTION ========== */}
      <section id="replaces" className="py-24 md:py-28 px-6 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.06),transparent_60%)]" />
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-14">
            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 mb-4" data-testid="replaces-badge">
              <Replace className="h-3 w-3 mr-2" /> Day-one impact
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              5 tools Recruitizr replaces
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-base leading-relaxed">
              Most recruitment firms pay for 5+ subscriptions that don&apos;t talk to each other. Recruitizr consolidates them into one intelligent platform — saving you money and sanity.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="replaces-grid">
            {[
              {
                icon: FileSpreadsheet,
                tool: 'Spreadsheets',
                desc: 'Kill the Excel trackers. Live pipelines, real-time data, zero manual entry.',
                color: 'from-emerald-500 to-teal-600',
                accent: 'text-emerald-400',
                borderAccent: 'border-emerald-500/20',
                bgAccent: 'bg-emerald-500/[0.05]'
              },
              {
                icon: Users,
                tool: 'CRM',
                desc: 'Client management, contacts, revenue tracking — built for staffing firms.',
                color: 'from-blue-500 to-indigo-600',
                accent: 'text-blue-400',
                borderAccent: 'border-blue-500/20',
                bgAccent: 'bg-blue-500/[0.05]'
              },
              {
                icon: Briefcase,
                tool: 'ATS',
                desc: 'Applicant tracking from requisition to closure in a single workflow.',
                color: 'from-violet-500 to-purple-600',
                accent: 'text-violet-400',
                borderAccent: 'border-violet-500/20',
                bgAccent: 'bg-violet-500/[0.05]'
              },
              {
                icon: Shield,
                tool: 'HRMS',
                desc: 'Attendance, leaves, onboarding, policies — all under one roof.',
                color: 'from-amber-500 to-orange-600',
                accent: 'text-amber-400',
                borderAccent: 'border-amber-500/20',
                bgAccent: 'bg-amber-500/[0.05]'
              },
              {
                icon: MonitorSmartphone,
                tool: 'Local Dashboards',
                desc: 'Real-time analytics for every role. No more Tableau or Power BI.',
                color: 'from-cyan-500 to-sky-600',
                accent: 'text-cyan-400',
                borderAccent: 'border-cyan-500/20',
                bgAccent: 'bg-cyan-500/[0.05]'
              },
            ].map((item, i) => (
              <div key={`lp-${i}`} className={`group relative p-5 rounded-2xl border ${item.borderAccent} ${item.bgAccent} text-center hover:scale-[1.03] transition-transform duration-300`} data-testid={`replaces-tool-${i}`}>
                <div className={`w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-lg`}>
                  <item.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className={`text-base font-bold mb-2 ${item.accent}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {item.tool}
                </h3>
                <p className="text-xs text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-sm text-slate-500">
              One subscription. One login. One platform that does it all.
            </p>
          </div>
        </div>
      </section>

      {/* ========== PROBLEM SECTION ========== */}
      <section id="problem" className="py-24 md:py-28 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <Badge className="bg-red-500/10 text-red-400 border-red-500/20 mb-4">
              <AlertTriangle className="h-3 w-3 mr-2" /> The problem
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Your team is drowning in spreadsheets
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-base leading-relaxed">
              Most recruitment firms still run on Excel trackers, scattered WhatsApp threads, and 4 different logins. The result? Missed follow-ups, lost candidates, zero visibility, and revenue left on the table.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: FileSpreadsheet, stat: '5+', label: 'Tools juggled daily', sub: 'Excel, email, job boards, CRM, WhatsApp' },
              { icon: Table2, stat: '40%', label: 'Time lost to admin', sub: 'Updating trackers, data entry, follow-ups' },
              { icon: Target, stat: '3x', label: 'Pipeline leakage', sub: 'Candidates lost between stages' },
              { icon: BarChart3, stat: '0', label: 'Real-time visibility', sub: 'No dashboards, no metrics, no accountability' },
            ].map((p, i) => (
              <div key={`lp-${i}`} className="p-5 rounded-2xl border border-red-500/10 bg-red-500/[0.03]">
                <p.icon className="h-5 w-5 text-red-400 mb-3" />
                <p className="text-3xl font-extrabold text-red-400 mb-1">{p.stat}</p>
                <p className="text-sm font-semibold text-white mb-1">{p.label}</p>
                <p className="text-xs text-slate-500">{p.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== 5 CORE USPs ========== */}
      <section id="usps" className="py-24 md:py-32 px-6 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(99,102,241,0.08),transparent_60%)]" />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <Badge className="bg-indigo-500/10 text-indigo-400 border-indigo-500/20 mb-4">{cms?.usps_header?.badge || 'Why Recruitizr'}</Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {cms?.usps_header?.title || 'One platform to drive your entire business'}
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">{cms?.usps_header?.description || 'Replace fragmented tools with a single, intelligent system built for how recruitment agencies actually work.'}</p>
          </div>

          {/* All-in-One — Common Info Block (no number) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 items-center">
            <div className="order-2 lg:order-1">
              <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{cms?.usps_main?.title || 'All-in-One Operating System'}</h3>
              <p className="text-slate-400 mb-5 leading-relaxed">
                {cms?.usps_main?.description || 'Combines CRM, ATS, and HRMS into a single platform tailored for recruitment agencies. Eliminate the chaos of juggling multiple tools — reduce costs, cut complexity, and give your team one source of truth.'}
              </p>
              <div className="flex flex-wrap gap-2">
                {(cms?.usps_main?.tags || ['Client CRM', 'Applicant Tracking', 'Resume Database', 'Pipeline Management', 'Revenue Tracking', 'Team Performance']).map(t => (
                  <span key={t} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-slate-300">{t}</span>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2 p-8 rounded-2xl border border-white/10 bg-gradient-to-br from-violet-500/[0.05] to-transparent">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: Layers, label: 'CRM', color: 'text-violet-400' },
                  { icon: Briefcase, label: 'ATS', color: 'text-indigo-400' },
                  { icon: Users, label: 'HRMS', color: 'text-cyan-400' },
                ].map(s => (
                  <div key={s.label} className="text-center p-4 rounded-xl bg-white/[0.03] border border-white/5">
                    <s.icon className={`h-8 w-8 mx-auto mb-2 ${s.color}`} />
                    <p className="text-sm font-semibold">{s.label}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-center">
                <ArrowRight className="h-5 w-5 text-slate-600 mx-auto rotate-90 mb-2" />
                <div className="p-3 rounded-xl bg-gradient-to-r from-violet-600/20 to-indigo-600/20 border border-violet-500/20">
                  <p className="text-sm font-bold text-violet-300">One unified platform</p>
                </div>
              </div>
            </div>
          </div>

          {/* USPs 2-5 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tile 01 — Recruiter-Centric AI */}
            <div className="p-8 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
              <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/20 mb-3 text-xs">01</Badge>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center mb-4 shadow-lg shadow-violet-500/20">
                <Brain className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Recruiter-Centric AI</h3>
              <p className="text-sm text-slate-400 leading-relaxed mb-4">
                RecruiterIQ isn&apos;t generic AI — it&apos;s built for your workflow. Auto-screen resumes, generate JDs in seconds, prep interview questions, and get candidate insights — all focused on making your recruiters faster and sharper.
              </p>
              <Button variant="outline" size="sm" className="border-violet-500/30 text-violet-400 hover:bg-violet-500/10" onClick={() => scrollTo('recruiteriq')}>
                Explore RecruiterIQ <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            </div>

            {/* Tile 02 — Data-Driven Productivity */}
            <div className="p-8 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
              <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/20 mb-3 text-xs">02</Badge>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center mb-4 shadow-lg shadow-cyan-500/20">
                <LineChart className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Data-Driven Productivity</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Real-time dashboards highlight recruiter performance, pipeline bottlenecks, and conversion metrics. Predictive analytics forecast hiring demand and recruiter bandwidth — so you plan ahead instead of firefighting.
              </p>
              <div className="mt-4 flex gap-4 text-center">
                {[{ val: 'Live', desc: 'Dashboards' }, { val: 'Funnel', desc: 'Metrics' }, { val: 'Predict', desc: 'Demand' }].map(m => (
                  <div key={m.desc} className="flex-1 p-2 rounded-lg bg-cyan-500/[0.05] border border-cyan-500/10">
                    <p className="text-xs font-bold text-cyan-400">{m.val}</p>
                    <p className="text-xs text-slate-500">{m.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Tile 03 — Compliance & Clean Data */}
            <div className="p-8 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
              <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 mb-3 text-xs">03</Badge>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/20">
                <ShieldCheck className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Privacy & Clean Data</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Built-in data subject rights — consent management, data export, and deletion workflows. AI-powered data hygiene keeps your CRM and ATS clean and duplicate-free, with India-resident storage and per-tenant isolation by design.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {['Privacy by Design', 'Data Export', 'Data Deletion', 'Consent Manager'].map(t => (
                  <span key={t} className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/10 text-xs text-emerald-400"><Check className="h-3 w-3" />{t}</span>
                ))}
              </div>
            </div>

            {/* Tile 04 — Culture & Adoption */}
            <div className="p-8 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
              <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 mb-3 text-xs">04</Badge>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-4 shadow-lg shadow-amber-500/20">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Culture & Adoption Support</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Built-in motivational nudges and vision-aligned messaging drive team adoption from day one. Adaptive dashboards personalize the recruiter experience by role — a generalist sees different priorities than a team lead.
              </p>
              <div className="mt-4 flex gap-3">
                {[{ icon: LayoutDashboard, label: 'Role-based views' }, { icon: Target, label: 'Goal nudges' }, { icon: UserCheck, label: 'Onboarding flows' }].map(f => (
                  <div key={f.label} className="flex items-center gap-1.5 text-xs text-amber-400/80">
                    <f.icon className="h-3.5 w-3.5" />{f.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== RECRUITER IQ ========== */}
      <section id="recruiteriq" className="py-24 md:py-32 px-6 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(124,58,237,0.12),transparent_60%)]" />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/20 mb-4">
              <Brain className="h-3 w-3 mr-2" /> RecruiterIQ
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              AI that boosts recruiter intelligence & decision-making
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">Powered by GPT-5.2. Not a gimmick — real AI tools that save your recruiters hours every day and make every decision sharper.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                icon: FileSearch, title: 'Resume Screening & Ranking',
                desc: 'AI scores every candidate against job requirements. Get a fit percentage, skill-gap analysis, and clear go/no-go recommendation — in seconds, not hours.',
                tag: 'Most Popular', metric: '70% less screening time'
              },
              {
                icon: Briefcase, title: 'JD Generator',
                desc: 'Turn three bullet points into a polished, inclusive, SEO-optimized job description. Post faster, attract better candidates, and standardize quality across your firm.',
                tag: 'Time Saver', metric: '5 hours saved per week'
              },
              {
                icon: MessageSquareText, title: 'Interview Question Generator',
                desc: 'Role-specific questions tailored to the candidate and position. Each comes with evaluation criteria and ideal answer hints — so even junior recruiters interview like veterans.',
                tag: 'Smart Prep', metric: 'Better interview quality'
              },
              {
                icon: UserRoundSearch, title: 'Candidate Insights',
                desc: 'Paste any resume and get an executive summary, skill extraction, seniority assessment, and ready-made talking points for the interview — all in one click.',
                tag: 'Deep Analysis', metric: 'Instant candidate briefs'
              },
            ].map((f, i) => (
              <div key={`lp-${i}`} className="group relative p-8 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-transparent hover:from-violet-500/[0.05] transition-all duration-500" data-testid={`iq-feature-${i}`}>
                <Badge className="absolute top-6 right-6 bg-violet-500/10 text-violet-400 border-violet-500/20 text-xs">{f.tag}</Badge>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center mb-5 shadow-lg shadow-violet-500/20">
                  <f.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-4">{f.desc}</p>
                <div className="flex items-center gap-2 text-xs text-emerald-400">
                  <TrendingUp className="h-3.5 w-3.5" />{f.metric}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== BEFORE / AFTER ========== */}
      <section className="py-20 px-6 border-y border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Before vs. After Recruitizr</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl bg-red-500/[0.03] border border-red-500/10">
              <p className="text-sm font-bold text-red-400 mb-4 uppercase tracking-wider">Without Recruitizr</p>
              <ul className="space-y-3">
                {[
                  '5+ disconnected tools for one workflow',
                  'Excel trackers with stale, duplicate data',
                  'Manual follow-ups falling through cracks',
                  'Zero real-time visibility on pipeline',
                  'Compliance headaches at audit time',
                  'Hours wasted writing JDs and screening CVs',
                ].map((item, i) => (
                  <li key={`lp-${i}`} className="flex items-start gap-2 text-sm text-slate-400"><X className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />{item}</li>
                ))}
              </ul>
            </div>
            <div className="p-6 rounded-2xl bg-emerald-500/[0.03] border border-emerald-500/10">
              <p className="text-sm font-bold text-emerald-400 mb-4 uppercase tracking-wider">With Recruitizr</p>
              <ul className="space-y-3">
                {[
                  'CRM + ATS + AI in one login',
                  'Clean, live data across every recruiter',
                  'Automated pipeline alerts and nudges',
                  'Real-time dashboards by role and team',
                  'Privacy-first workflows with built-in data rights',
                  'AI writes JDs and screens resumes in seconds',
                ].map((item, i) => (
                  <li key={`lp-${i}`} className="flex items-start gap-2 text-sm text-slate-300"><Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ========== REQUEST A DEMO ========== */}
      <section id="demo" className="py-24 md:py-32 px-6 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(124,58,237,0.1),transparent_60%)]" />
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Copy */}
            <div>
              <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/20 mb-4">
                <Send className="h-3 w-3 mr-2" /> Request a Demo
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                See Recruitizr in action
              </h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Get a personalized walkthrough of how Recruitizr can replace your existing tools, boost your team&apos;s productivity, and give you real-time visibility across every mandate.
              </p>
              <div className="space-y-3">
                {[
                  'Personalized demo for your team size and workflow',
                  'See RecruiterIQ AI in action with your own JDs',
                  'Custom migration plan from your current tools',
                  'No commitment — just a conversation',
                ].map((item, i) => (
                  <div key={`lp-${i}`} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />{item}
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Form */}
            <div className="p-8 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-sm">
              {demoSubmitted ? (
                <div className="text-center py-8" data-testid="demo-success">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Check className="h-8 w-8 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Thank you!
                  </h3>
                  <p className="text-slate-400 text-sm">
                    We&apos;ve received your request. Our team will reach out within 24 hours to schedule your personalized demo.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-6 border-slate-700 text-slate-300"
                    onClick={() => setDemoSubmitted(false)}
                  >
                    Submit Another Request
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleDemoSubmit} className="space-y-4" data-testid="demo-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Full Name *</Label>
                      <div className="relative mt-1.5">
                        <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600" />
                        <Input
                          data-testid="demo-name"
                          value={demoForm.name}
                          onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                          placeholder="Jane Smith"
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus:border-violet-500"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Work Email *</Label>
                      <div className="relative mt-1.5">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600" />
                        <Input
                          data-testid="demo-email"
                          type="email"
                          value={demoForm.email}
                          onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                          placeholder="jane@company.com"
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus:border-violet-500"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Company *</Label>
                      <div className="relative mt-1.5">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600" />
                        <Input
                          data-testid="demo-company"
                          value={demoForm.company}
                          onChange={(e) => setDemoForm({ ...demoForm, company: e.target.value })}
                          placeholder="Acme Staffing"
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus:border-violet-500"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Phone</Label>
                      <div className="relative mt-1.5">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600" />
                        <Input
                          data-testid="demo-phone"
                          value={demoForm.phone}
                          onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                          placeholder="+1 (555) 000-0000"
                          className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus:border-violet-500"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Team Size</Label>
                    <select
                      data-testid="demo-team-size"
                      value={demoForm.team_size}
                      onChange={(e) => setDemoForm({ ...demoForm, team_size: e.target.value })}
                      className="mt-1.5 w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm focus:border-violet-500 focus:outline-none"
                    >
                      <option value="" className="bg-slate-900">Select team size</option>
                      <option value="1-5" className="bg-slate-900">1-5 recruiters</option>
                      <option value="6-15" className="bg-slate-900">6-15 recruiters</option>
                      <option value="16-50" className="bg-slate-900">16-50 recruiters</option>
                      <option value="50+" className="bg-slate-900">50+ recruiters</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Message</Label>
                    <textarea
                      data-testid="demo-message"
                      value={demoForm.message}
                      onChange={(e) => setDemoForm({ ...demoForm, message: e.target.value })}
                      placeholder="Tell us about your current tools and what you're looking for..."
                      rows={3}
                      className="mt-1.5 w-full rounded-md bg-white/5 border border-white/10 text-white px-3 py-2 text-sm placeholder:text-slate-600 focus:border-violet-500 focus:outline-none resize-none"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={demoSubmitting}
                    className="w-full bg-violet-600 hover:bg-violet-500 text-white font-semibold py-3 rounded-full shadow-[0_0_30px_rgba(124,58,237,0.3)]"
                    data-testid="demo-submit-btn"
                  >
                    {demoSubmitting ? 'Submitting...' : 'Request a Demo'}
                    <Send className="ml-2 h-4 w-4" />
                  </Button>
                  <p className="text-xs text-slate-600 text-center">No spam. We&apos;ll respond within 24 hours.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ========== CTA ========== */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {cms?.cta_section?.title || 'Ready to ditch the spreadsheets?'}
          </h2>
          <p className="text-slate-400 mb-8 text-lg">{cms?.cta_section?.description || 'Join recruitment firms that run their entire business on Recruitizr — one platform, zero chaos.'}</p>
          <Button
            size="lg"
            className="bg-violet-600 hover:bg-violet-500 text-white rounded-full px-10 py-6 text-base font-semibold shadow-[0_0_40px_rgba(124,58,237,0.3)]"
            onClick={() => scrollTo('demo')}
            data-testid="cta-get-started-btn"
          >
            {cms?.cta_section?.button_text || 'Request a Demo'} <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <span className="text-2xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Recruitizr</span>
              </div>
              <p className="text-sm text-slate-400 font-medium">Built by recruiters, for recruiters.</p>
            </div>

            {/* Connect with us */}
            <div>
              <h4 className="text-sm font-bold text-white mb-4">Connect with us</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li><a href={`mailto:${cms?.footer?.email || 'info@recruitizr.ai'}`} className="flex items-center gap-2 hover:text-violet-400 transition-colors"><Mail className="h-4 w-4" /> {cms?.footer?.email || 'info@recruitizr.ai'}</a></li>
                <li>
                  <a
                    href={cms?.footer?.linkedin_url || 'https://www.linkedin.com/company/recruitizr/?viewAsMember=true'}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Recruitizr on LinkedIn"
                    data-testid="footer-linkedin-link"
                    className="flex items-center gap-2 hover:text-violet-400 transition-colors"
                  >
                    <Linkedin className="h-4 w-4" /> LinkedIn
                  </a>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-sm font-bold text-white mb-4">Company</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                {(() => {
                  // CMS data is authoritative for label/url overrides, but
                  // /developers is a required link — append it if missing so
                  // saved CMS docs don't silently hide it.
                  const cmsLinks = Array.isArray(cms?.footer?.company_links) ? cms.footer.company_links : null;
                  const defaults = [
                    { label: 'About us', url: '/about' },
                    { label: 'Help Centre', url: '/help' },
                    { label: 'Developers', url: '/developers' },
                    { label: 'Contact us', url: '#demo' },
                  ];
                  let links = cmsLinks || defaults;
                  if (!links.some(l => (l?.url || '').replace(/\/$/, '') === '/developers')) {
                    // Insert Developers just before Contact us if present, else at end
                    const contactIdx = links.findIndex(l => /contact/i.test(l?.label || ''));
                    const dev = { label: 'Developers', url: '/developers' };
                    links = contactIdx >= 0
                      ? [...links.slice(0, contactIdx), dev, ...links.slice(contactIdx)]
                      : [...links, dev];
                  }
                  return links.map((link, i) => (
                    <li key={link.label || `link-${i}`}>{link.url?.startsWith('#') ? (
                      <button onClick={() => scrollTo(link.url.replace('#', ''))} className="hover:text-violet-400 transition-colors">{link.label}</button>
                    ) : (
                      <a href={link.url} className="hover:text-violet-400 transition-colors" data-testid={link.url === '/developers' ? 'footer-developers-link' : undefined}>{link.label}</a>
                    )}</li>
                  ));
                })()}
              </ul>
            </div>

            {/* Terms & policy */}
            <div>
              <h4 className="text-sm font-bold text-white mb-4">Terms & policy</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                {(cms?.footer?.policy_links || [{ label: 'Terms & conditions', url: '/terms' }, { label: 'Privacy policy', url: '/privacy' }]).map((link, i) => (
                  <li key={link.label || `plink-${i}`}><a href={link.url} className="hover:text-violet-400 transition-colors">{link.label}</a></li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t border-white/5 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-600">&copy; 2026 Recruitizr. All rights reserved.</p>
            <a
              href={cms?.footer?.linkedin_url || 'https://www.linkedin.com/company/recruitizr/?viewAsMember=true'}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Recruitizr on LinkedIn"
              data-testid="footer-linkedin-icon"
              className="text-slate-500 hover:text-violet-400 transition-colors"
            >
              <Linkedin className="h-5 w-5" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
